import {
    type UserDefinedObjectTemplate,
    UserDefinedObjectTemplateStatus,
} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplate";
import type {WorkspaceCapability} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    type OttopilotEventContainerTemplateConfiguration,
    ottopilotEventContainerTemplateConfigurationValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTemplateConfiguration";
import {z as zod} from "zod";

/** Domain view referencing UDO metadata and capability business configuration. */
export type OttopilotEventContainerTemplate = {
    configuration: OttopilotEventContainerTemplateConfiguration;
    createdAt: Iso8601DateTime;
    createdBy: Uuid;
    description: string | null;
    displayName: string;
    id: Uuid;
    status: OttopilotEventContainerTemplateStatus;
    udoTemplateId: Uuid;
    updatedAt: Iso8601DateTime;
    updatedBy: Uuid;
};

export enum OttopilotEventContainerTemplateStatus {
    active = "ca4db5b3-46df-4ed3-a5ba-f9aba320f98b",
    archived = "9fa84525-1324-48d4-8a76-ea7e37529620",
}

// Validate the required physical OU column against the linked UDO metadata in the consuming service.
export const ottopilotEventContainerTemplateValidator: zod.ZodType<OttopilotEventContainerTemplate> = zod.strictObject({
    configuration: ottopilotEventContainerTemplateConfigurationValidator,
    createdAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    description: zod.string().nullable(),
    displayName: zod.string(),
    id: uuidZodValidator,
    status: zod.enum(OttopilotEventContainerTemplateStatus),
    udoTemplateId: uuidZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    updatedBy: uuidZodValidator,
});

export type OttopilotEventContainerTemplateCudLog = OttopilotEventContainerTemplate;

export const ottopilotEventContainerTemplateCudLogValidator: zod.ZodType<OttopilotEventContainerTemplateCudLog> =
    ottopilotEventContainerTemplateValidator;

/** Compose all configured templates, including archived ones, from validated capability and UDO objects.
 * Names, descriptions, statuses and audit fields come from the linked UDO template.
 * Missing bindings or UDO metadata are errors; callers must supply archived UDO templates too.
 */
export function getOttopilotEventContainerTemplatesFromWorkspaceCapability({allUdoTemplates, workspaceCapability}: {
    allUdoTemplates: ReadonlyArray<UserDefinedObjectTemplate>;
    workspaceCapability: WorkspaceCapability<WorkspaceCapabilityType.ottopilotEventContainers>;
}): Array<OttopilotEventContainerTemplate> {
    const entries = workspaceCapability.configuration.eventContainerTemplates;
    const bindings = workspaceCapability.udoBindings.eventContainerUdoTemplates;
    const workspaceTemplates = new Map(allUdoTemplates
        .filter((template) => template.workspaceId === workspaceCapability.workspaceId)
        .map((template) => [template.id, template]));

    for (const templateId of Object.keys(bindings)) {
        if (entries[uuidZodValidator.parse(templateId)] === undefined) {
            throw new Error(`OttopilotEventContainer template ${templateId} has a UDO binding but no business configuration`);
        }
    }

    return Object.entries(entries).map(([templateId, entry]): OttopilotEventContainerTemplate => {
        const id = uuidZodValidator.parse(templateId);
        const binding = bindings[id];
        if (binding === undefined) {
            throw new Error(`OttopilotEventContainer template ${id} has no UDO binding`);
        }
        const udoTemplate = workspaceTemplates.get(binding.udoTemplateId);
        if (udoTemplate === undefined) {
            throw new Error(`UDO template ${binding.udoTemplateId} for OttopilotEventContainer template ${id} was not found in workspace ${workspaceCapability.workspaceId}`);
        }

        return {
            configuration: entry.configuration,
            createdAt: udoTemplate.createdAt,
            createdBy: udoTemplate.createdBy,
            description: udoTemplate.description,
            displayName: udoTemplate.displayName,
            id,
            status: udoTemplate.status === UserDefinedObjectTemplateStatus.active
                ? OttopilotEventContainerTemplateStatus.active
                : OttopilotEventContainerTemplateStatus.archived,
            udoTemplateId: udoTemplate.id,
            updatedAt: udoTemplate.updatedAt,
            updatedBy: udoTemplate.updatedBy,
        };
    });
}
